import "./App.css";
import { 
  Zap, 
  Home, 
  Wrench, 
  PenTool, 
  Cable, 
  Lightbulb, 
  Smartphone,
  Mail,
  Instagram,
  MapPin,
  MessageCircle,
  Phone
} from "lucide-react";

// Contact data
const CONTACT = {
  whatsapp: "5545998313744",
  whatsappMessage: "Olá! Gostaria de um orçamento.",
  email: "Tineelli3744@gmail.com",
  instagram: "willtineelli",
  location: "Toledo/PR e região"
};

// Logo URL
const LOGO_URL = "https://res.cloudinary.com/dosexdjvz/image/upload/v1775586597/Logo_de_eletricista_Tinelli_com_raio_dtbueb.webp";

// Services data
const SERVICES = [
  { title: "Padrão de Energia", description: "Instalação e regularização de entrada de energia", icon: Zap },
  { title: "Instalações Residenciais", description: "Projetos completos para sua casa", icon: Home },
  { title: "Instalações Comerciais", description: "Soluções para empresas e comércios", icon: Wrench },
  { title: "Projetos Elétricos", description: "Planejamento técnico e execução", icon: PenTool },
  { title: "Troca de Fiação", description: "Substituição de fiação e disjuntores", icon: Cable },
  { title: "Iluminação de Ambientes", description: "Design de iluminação moderno", icon: Lightbulb },
  { title: "Automação", description: "Automação residencial e comercial", icon: Smartphone },
];

// WhatsApp URL generator
const getWhatsAppUrl = () => {
  const message = encodeURIComponent(CONTACT.whatsappMessage);
  return `https://wa.me/${CONTACT.whatsapp}?text=${message}`;
};

// Hero Section Component
const HeroSection = () => (
  <section 
    data-testid="hero-section"
    className="relative min-h-[70vh] flex flex-col items-center justify-center px-4 py-12"
  >
    {/* Background with overlay */}
    <div className="absolute inset-0 z-0">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1200&q=80')` 
        }}
      />
      <div className="hero-overlay absolute inset-0" />
    </div>
    
    {/* Content */}
    <div className="relative z-10 flex flex-col items-center text-center max-w-md mx-auto">
      {/* Logo */}
      <div className="animate-fadeInUp opacity-0 mb-6">
        <img 
          src={LOGO_URL} 
          alt="Tinelli Eletricista Logo" 
          className="w-96 h-96 object-contain logo-glow"
          data-testid="hero-logo"
        />
      </div>
      
      {/* Title */}
      <h1 
        className="animate-fadeInUp opacity-0 animate-delay-100 text-4xl sm:text-5xl font-black tracking-tighter text-white mb-3"
        data-testid="hero-title"
      >
        Tinelli <span className="text-[#FF5500]">Eletricista</span>
      </h1>
      
      {/* Location */}
      <div className="animate-fadeInUp opacity-0 animate-delay-200 flex items-center gap-2 text-[#A1A1AA] mb-4">
        <MapPin className="w-4 h-4 text-[#FF5500]" />
        <span className="text-sm font-medium">{CONTACT.location}</span>
      </div>
      
      {/* Description */}
      <p 
        className="animate-fadeInUp opacity-0 animate-delay-300 text-base text-[#A1A1AA] mb-8 leading-relaxed"
        data-testid="hero-description"
      >
        Serviços elétricos de qualidade para sua casa ou empresa. 
        Segurança, eficiência e atendimento profissional.
      </p>
      
      {/* CTA Button */}
      <a
        href={getWhatsAppUrl()}
        target="_blank"
        rel="noopener noreferrer"
        className="animate-fadeInUp opacity-0 animate-delay-400 w-full max-w-xs rounded-xl py-4 px-6 text-center font-bold text-lg flex justify-center items-center gap-3 bg-[#FF5500] text-white hover:bg-[#E64D00] btn-glow transition-all duration-300"
        data-testid="hero-cta-btn"
      >
        <Phone className="w-5 h-5" />
        Solicite seu Orçamento
      </a>
    </div>
  </section>
);

// Service Card Component
const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  const delayClass = `animate-delay-${(index + 1) * 100}`;
  
  return (
    <div 
      className={`service-card shine-effect animate-fadeInUp opacity-0 ${delayClass} rounded-2xl border border-white/10 bg-[#121212] p-4 flex items-center gap-4`}
      data-testid={`service-card-${index}`}
    >
      <div className="icon-container flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center">
        <Icon className="w-6 h-6 text-[#FF5500]" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-white font-semibold text-base tracking-tight">{service.title}</h3>
        <p className="text-[#A1A1AA] text-sm mt-0.5">{service.description}</p>
      </div>
    </div>
  );
};

// Services Section Component
const ServicesSection = () => (
  <section 
    data-testid="services-section"
    className="px-4 py-8"
  >
    <div className="max-w-md mx-auto">
      {/* Section Header */}
      <div className="text-center mb-8">
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF5500]">
          Nossos Serviços
        </span>
        <h2 className="text-2xl font-bold tracking-tight text-white mt-2">
          Soluções Completas em Eletricidade
        </h2>
      </div>
      
      {/* Services Grid */}
      <div className="flex flex-col gap-3">
        {SERVICES.map((service, index) => (
          <ServiceCard key={index} service={service} index={index} />
        ))}
      </div>
    </div>
  </section>
);

// Contact Section Component
const ContactSection = () => (
  <section 
    data-testid="contact-section"
    className="px-4 py-8"
  >
    <div className="max-w-md mx-auto">
      {/* Section Header */}
      <div className="text-center mb-8">
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF5500]">
          Contato
        </span>
        <h2 className="text-2xl font-bold tracking-tight text-white mt-2">
          Entre em Contato
        </h2>
      </div>
      
      {/* Contact Buttons */}
      <div className="flex flex-col gap-3">
        {/* Email Button */}
        <a
          href={`mailto:${CONTACT.email}`}
          className="service-card shine-effect rounded-xl w-full py-4 px-6 flex justify-center items-center gap-3 bg-[#121212] border border-white/10 text-white hover:bg-[#1A1A1A] transition-all duration-300"
          data-testid="contact-email-btn"
        >
          <Mail className="w-5 h-5 text-[#FF5500]" />
          <span className="font-bold">Enviar E-mail</span>
        </a>
        
        {/* Instagram Button */}
        <a
          href={`https://instagram.com/${CONTACT.instagram}`}
          target="_blank"
          rel="noopener noreferrer"
          className="service-card shine-effect rounded-xl w-full py-4 px-6 flex justify-center items-center gap-3 bg-[#121212] border border-white/10 text-white hover:bg-[#1A1A1A] transition-all duration-300"
          data-testid="contact-instagram-btn"
        >
          <Instagram className="w-5 h-5 text-[#FF5500]" />
          <span className="font-bold">@{CONTACT.instagram}</span>
        </a>
        
        {/* WhatsApp Button (secondary) */}
        <a
          href={getWhatsAppUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-xl w-full py-4 px-6 text-center font-bold flex justify-center items-center gap-3 bg-[#FF5500] text-white hover:bg-[#E64D00] btn-glow transition-all duration-300 mt-2"
          data-testid="contact-whatsapp-btn"
        >
          <MessageCircle className="w-5 h-5" />
          Fale Conosco no WhatsApp
        </a>
      </div>
    </div>
  </section>
);

// Footer Component
const Footer = () => (
  <footer 
    data-testid="footer"
    className="px-4 py-8 text-center border-t border-white/10"
  >
    <div className="max-w-md mx-auto">
      <p className="text-[#A1A1AA] text-sm">
        © {new Date().getFullYear()} Tinelli Eletricista. Todos os direitos reservados.
      </p>
      <p className="text-[#A1A1AA] text-xs mt-2 flex items-center justify-center gap-1">
        <MapPin className="w-3 h-3" />
        {CONTACT.location}
      </p>
    </div>
  </footer>
);

// Floating WhatsApp Button Component
const FloatingWhatsApp = () => (
  <a
    href={getWhatsAppUrl()}
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300 animate-pulse-orange animate-float"
    data-testid="floating-whatsapp-btn"
    aria-label="Abrir WhatsApp"
  >
    <svg 
      viewBox="0 0 24 24" 
      className="w-8 h-8 fill-white"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-[#050505] pb-24" data-testid="app-container">
      <HeroSection />
      <ServicesSection />
      <ContactSection />
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
